import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Lock } from "lucide-react";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">{label}</span>
    <input
      {...props}
      className="mt-2 w-full bg-transparent border border-[#E5E1D8] rounded-none px-4 py-3 font-sans text-sm text-[#1A1A1A] focus:border-[#1A1A1A] outline-none transition-colors"
    />
  </label>
);

const Checkout = () => {
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user?.name || "", phone: "", address: "", city: "", pincode: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const finalize = async (orderId, extra = {}) => {
    const { data } = await api.post("/orders/verify", { order_id: orderId, ...extra });
    clear();
    setDone(data);
    toast.success("Payment successful! Order confirmed.");
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (!form.name || !form.phone || !form.address || !form.city || !form.pincode) {
      toast.error("Please fill in all shipping details.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items,
        shipping_name: form.name,
        shipping_phone: form.phone,
        shipping_address: form.address,
        shipping_city: form.city,
        shipping_pincode: form.pincode,
      });

      if (data.demo_mode) {
        await finalize(data.order_id);
      } else {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load payment gateway."); setLoading(false); return; }
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id,
          amount: data.amount,
          currency: data.currency,
          name: "Anhad Collections",
          order_id: data.razorpay_order_id,
          prefill: { name: form.name, email: user?.email, contact: form.phone },
          theme: { color: "#8C3A3A" },
          handler: async (res) => {
            await finalize(data.order_id, {
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_signature: res.razorpay_signature,
            });
          },
          modal: { ondismiss: () => setLoading(false) },
        });
        rzp.open();
        return;
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed.");
    }
    setLoading(false);
  };

  if (done)
    return (
      <div className="max-w-2xl mx-auto px-6 py-28 text-center">
        <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <CheckCircle2 size={56} className="text-[#8C3A3A] mx-auto" />
        </motion.div>
        <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tight text-[#1A1A1A] mt-8">Order Confirmed</h1>
        <p className="font-sans text-sm text-[#595959] mt-5">Thank you, {form.name.split(" ")[0]}. We've received your order of {inr(done.total)}.</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959] mt-3">Order #{done.id.slice(0, 8)} · {done.demo_mode ? "Demo Payment" : "Paid"}</p>
        <div className="flex gap-3 justify-center mt-10">
          <button onClick={() => navigate("/orders")} data-testid="view-orders-btn" className="bg-[#1A1A1A] text-[#F9F8F6] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#8C3A3A] transition-colors">View Orders</button>
          <button onClick={() => navigate("/shop")} className="border border-[#1A1A1A] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#1A1A1A] hover:text-[#F9F8F6] transition-colors">Keep Shopping</button>
        </div>
      </div>
    );

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 grid md:grid-cols-12 gap-16">
      <div className="md:col-span-7">
        <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tight text-[#1A1A1A] mb-12">Checkout</h1>
        <form onSubmit={handlePay} className="space-y-6" data-testid="checkout-form">
          <Field label="Full Name" value={form.name} onChange={set("name")} data-testid="ship-name" />
          <Field label="Phone" value={form.phone} onChange={set("phone")} data-testid="ship-phone" />
          <Field label="Address" value={form.address} onChange={set("address")} data-testid="ship-address" />
          <div className="grid grid-cols-2 gap-6">
            <Field label="City" value={form.city} onChange={set("city")} data-testid="ship-city" />
            <Field label="Pincode" value={form.pincode} onChange={set("pincode")} data-testid="ship-pincode" />
          </div>
          <button type="submit" disabled={loading} data-testid="pay-btn" className="w-full bg-[#1A1A1A] text-[#F9F8F6] py-5 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-[#8C3A3A] transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2">
            <Lock size={14} /> {loading ? "Processing…" : `Pay ${inr(total)}`}
          </button>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#595959] text-center">Secured by Razorpay · Demo mode active</p>
        </form>
      </div>

      <div className="md:col-span-5">
        <div className="border border-[#E5E1D8] p-8 md:sticky md:top-28">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C3A3A] mb-6">Your Order</p>
          <div className="space-y-4">
            {items.map((i) => (
              <div key={`${i.product_id}-${i.size}`} className="flex justify-between gap-4">
                <span className="font-sans text-sm text-[#1A1A1A]">{i.name} <span className="text-[#595959]">×{i.quantity} · {i.size}</span></span>
                <span className="font-mono text-sm whitespace-nowrap">{inr(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[#E5E1D8] mt-6 pt-6 flex justify-between items-baseline">
            <span className="font-serif text-2xl text-[#1A1A1A]">Total</span>
            <span className="font-mono text-lg text-[#1A1A1A]">{inr(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
